import { Box } from "@chakra-ui/react";
import React, { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import {
  selectAppDisable,
  selectCurrentDevice,
  selectpaypalClientId,
} from "stores/auth/authSlice";
import { useAppSelector } from "hooks/store/hooks";
import PrivacyAndPolicy from "pages/PrivacyAndPolicy";
import UnsubscribeEmail from "pages/RegisterNewUserPage/UnsubscribeEmail";

const ImagePreview = lazy(() =>
  import("pages/RecordingListPage/ImageList/ImagePreview")
);
const PhotoGalleryPreview = lazy(() =>
  import("pages/ChinaUIPhotoPage/ImagePreview")
);
const KeyboardsLogs = lazy(() => import("pages/chinaKeyboardUsagelog"));
const MobileFilePhoto = lazy(() => import("pages/ChinaUIPhotoPage"));
const MobileFileVideo = lazy(() => import("pages/ChinaUIVideoPage"));
const VideoPreview = lazy(() => import("pages/ChinaUIVideoPage/VideoPreview"));
const Profile = lazy(() => import("pages/ProfilePage"));
const DataManagementPage = lazy(() => import("pages/DataManagementPage"));
const ActivationPage = lazy(() => import("pages/ActivationPage"));
const FAQs = lazy(() => import("pages/ProfilePage/FAQs"));
const InvitationRecord = lazy(() =>
  import("pages/ProfilePage/InvitationRecord")
);
const SDCardPage = lazy(() => import("pages/SDCardPage"));
const MemberSubscription = lazy(() =>
  import("pages/ProfilePage/MemberSubscription")
);
const UsageAgreement = lazy(() => import("pages/ProfilePage/UsageAgreement"));
const UserGuide = lazy(() => import("pages/ProfilePage/UserGuide"));
const UserSharing = lazy(() => import("pages/ProfilePage/UserSharing"));
const VersionUpdate = lazy(() => import("pages/ProfilePage/VersionUpdate"));
const ScreenLimit = lazy(() => import("pages/ScreenLimitpage"));
const BrowsingHistory = lazy(() => import("pages/ChinaHistoryBrowsing"));
const NewInterception = lazy(() =>
  import("pages/ChinaHistoryBrowsing/NewInterception")
);
const ScreenShots = lazy(() => import("pages/ChinaScreenshotspage"));
const Operationlog = lazy(() => import("pages/ChinaOperationLogPage"));
const SocialAppList = lazy(() => import("pages/ChinaSocialAppPage"));
const ForgotPassword = lazy(() => import("pages/ChinaForgotPasswordaPage"));
const ChinaContact = lazy(() => import("pages/ChinaContactPage"));
const RegisterUser = lazy(() => import("pages/RegisterNewUserPage"));
const Recordinglist = lazy(() => import("pages/RecordingListPage"));
const Index = lazy(() => import("pages/index"));
const LoginWithOTP = lazy(() => import("pages/LoginWithOTP"));
const PreDashboardPage = lazy(() => import("pages/PreDashboardPage/index"));
const DashboardPage = lazy(() => import("pages/DashboardPage/index"));
const ConsolePage = lazy(() => import("pages/ConsolePage/index"));
const MobilePhoneFilePage = lazy(() =>
  import("pages/MobilePhoneFilePage/index")
);
const CallLogsPage = lazy(() => import("pages/CallLogsPage/index"));
const SchedulePage = lazy(() => import("pages/SchedulePage"));
const SMSPage = lazy(() => import("pages/SMSPage/index"));
const PhotoPage = lazy(() => import("pages/PhotoPage/index"));
const ClipboardPage = lazy(() => import("pages/ClipboardPage/index"));
const LivePanel = lazy(() => import("pages/LivePanel"));
const ApplicationListPage = lazy(() =>
  import("pages/ChinaApplicationList/index")
);
const AppsRecordingPage = lazy(() => import("pages/AppsRecordingPage"));
const VerificationPage = lazy(() => import("pages/VerificationPage"));
const AppsPage = lazy(() => import("pages/AppsPage"));
const TransferPage = lazy(() => import("pages/TransferPage"));
const NotificationsPage = lazy(() => import("pages/NotificationsPage"));
const TimelinePage = lazy(() => import("pages/TimelinePage"));
const LocationHistory = lazy(() => import("pages/AllLocationHistoryPage"));
const SubscriptionPage = lazy(() => import("pages/SubscriptionPage"));
const SetupPage = lazy(() => import("pages/SetupPage/index"));
const SettingsPage = lazy(() => import("pages/SettingsPage/index"));
const AppUsagePage = lazy(() => import("pages/ApplicationOverview"));
const ScreenTimePage = lazy(() => import("pages/ScreenTimePage"));
const PaymentPage = lazy(() => import("pages/PaymentPage"));
const PaymentSuccessPage = lazy(() => import("pages/PaymentSuccessPage"));
const DeviceInformationPage = lazy(() =>
  import("pages/DeviceInformationPage/index")
);

const Router = () => {
  // Select values from the Redux store
  const { currentDevice } = useAppSelector((state) => ({
    currentDevice: selectCurrentDevice(state),
    clientId: selectpaypalClientId(state),
    appDisable: selectAppDisable(state),
  }));

  return (
    <Suspense
      fallback={
        <Box textAlign="center" marginY="100px">
          {/* <Spinner /> */}
        </Box>
      }
    >
      <Routes>
        {/* Basic Routes */}
        <Route path="/" element={<Index />} />
        <Route path="/loginWithOtp" element={<LoginWithOTP />} />
        <Route path="/privacy-and-policy" element={<PrivacyAndPolicy/>} />
        <Route path="/UnsubscribeEmail" element={<UnsubscribeEmail />} />
        {/* Routes with Conditional Rendering */}
        {[
          {
            path: "/pre-dashboard",
            component: <PreDashboardPage currentDevice={currentDevice} />,
          },
          {
            path: "/dashboard",
            component: <DashboardPage currentDevice={currentDevice} />,
          },
          {
            path: "/console",
            component: <ConsolePage currentDevice={currentDevice} />,
          },
          {
            path: "/map",
            component: <LocationHistory currentDevice={currentDevice} />,
          },
          {
            path: "/mobilePhoneFile",
            component: <MobilePhoneFilePage currentDevice={currentDevice} />,
          },
          {
            path: "/calllogs",
            component: <CallLogsPage currentDevice={currentDevice} />,
          },
          {
            path: "/sms",
            component: <SMSPage currentDevice={currentDevice} />,
          },
          {
            path: "/contacts",
            component: <ChinaContact currentDevice={currentDevice} />,
          },
          {
            path: "/photo",
            component: <PhotoPage currentDevice={currentDevice} />,
          },
          {
            path: "/livepanel",
            component: <LivePanel currentDevice={currentDevice} />,
          },
          {
            path: "/appsRecording",
            component: <AppsRecordingPage currentDevice={currentDevice} />,
          },
          {
            path: "/dataManagement",
            component: <DataManagementPage currentDevice={currentDevice} />,
          },
          {
            path: "/clipboard",
            component: <ClipboardPage currentDevice={currentDevice} />,
          },
          {
            path: "/applicationList",
            component: <AppsPage currentDevice={currentDevice} />,
          },
          {
            path: "/notifications",
            component: <NotificationsPage currentDevice={currentDevice} />,
          },
          {
            path: "/transfer",
            component: <TransferPage currentDevice={currentDevice} />,
          },
          {
            path: "/timeline",
            component: <TimelinePage currentDevice={currentDevice} />,
          },
          {
            path: "/setup",
            component: <SettingsPage currentDevice={currentDevice} />,
          },
          {
            path: "/DeviceInformation",
            component: <DeviceInformationPage currentDevice={currentDevice} />,
          },
          {
            path: "/Profile",
            component: <Profile currentDevice={currentDevice} />,
          },
          {
            path: "/Profile/terms-of-use",
            component: <UsageAgreement currentDevice={currentDevice} />,
          },
          {
            path: "/subscription",
            component: <SubscriptionPage currentDevice={currentDevice} />,
          },
          {
            path: "/Profile/subscription",
            component: <MemberSubscription currentDevice={currentDevice} />,
          },
          {
            path: "/profile/user-guide",
            component: <UserGuide currentDevice={currentDevice} />,
          },
          {
            path: "/profile/version-updates",
            component: <VersionUpdate currentDevice={currentDevice} />,
          },
          {
            path: "/profile/faq",
            component: <FAQs currentDevice={currentDevice} />,
          },
          {
            path: "/profile/user-sharing",
            component: <UserSharing currentDevice={currentDevice} />,
          },
          {
            path: "/profile/user-sharing/invitation-record",
            component: <InvitationRecord currentDevice={currentDevice} />,
          },
          {
            path: "/ApplicationOverview",
            component: <AppUsagePage currentDevice={currentDevice} />,
          },
          {
            path: "/screenLimit",
            component: <ScreenTimePage currentDevice={currentDevice} />,
          },
          {
            path: "/payment",
            component: <PaymentPage currentDevice={currentDevice} />,
          },
          {
            path: "/paymentSuccess",
            component: <PaymentSuccessPage currentDevice={currentDevice} />,
          },
          {
            path: "/chinaScreenLimit",
            component: <ScreenLimit currentDevice={currentDevice} />,
          },
          {
            path: "/keyboardslogs",
            component: <KeyboardsLogs currentDevice={currentDevice} />,
          },
          {
            path: "/schedule",
            component: <SchedulePage currentDevice={currentDevice} />,
          },
          {
            path: "/sdCard",
            component: <SDCardPage currentDevice={currentDevice} />,
          },
          {
            path: "/mobileFilePhoto",
            component: <MobileFilePhoto currentDevice={currentDevice} />,
          },
          {
            path: "/ImagePreview",
            component: <ImagePreview currentDevice={currentDevice} />,
          },
          {
            path: "/PhotoGalleryPreview",
            component: <PhotoGalleryPreview currentDevice={currentDevice} />,
          },
          {
            path: "/mobileFileVideo",
            component: <MobileFileVideo currentDevice={currentDevice} />,
          },
          {
            path: "/VideoPreview",
            component: <VideoPreview currentDevice={currentDevice} />,
          },
          {
            path: "/browsingHistory",
            component: <BrowsingHistory currentDevice={currentDevice} />,
          },
          {
            path: "/browsingHistory/newInterception",
            component: <NewInterception currentDevice={currentDevice} />,
          },
          {
            path: "/screenShots",
            component: <ScreenShots currentDevice={currentDevice} />,
          },
          {
            path: "/operation-log",
            component: <Operationlog currentDevice={currentDevice} />,
          },
          {
            path: "/ChinaApplciationList",
            component: <ApplicationListPage currentDevice={currentDevice} />,
          },
          {
            path: "/SocialApps",
            component: <SocialAppList currentDevice={currentDevice} />,
          },
          {
            path:"/verification",
            component:<VerificationPage currentDevice={currentDevice} />
          },
          {
            path:"/activation",
            component:<ActivationPage currentDevice={currentDevice} />
          },
          {
            path:"/activation/:code",
            component:<ActivationPage currentDevice={currentDevice} />
          },
          {
            path: "/forgotpassword",
            component: <ForgotPassword currentDevice={currentDevice} />,
          },
          {
            path: "/ChinaContact",
            component: <ChinaContact currentDevice={currentDevice} />,
          },
          {
            path: "/chinaSetup",
            component: <SetupPage currentDevice={currentDevice} />,
          },
          {
            path: "/registeruser",
            component: <RegisterUser currentDevice={currentDevice} />,
          },
          {
            path: "/recordinglist",
            component: <Recordinglist currentDevice={currentDevice} />,
          },
        ].map(({ path, component }) => (
          <Route key={path} path={path} element={component} />
        ))}
        <Route
          path="/payment/:paymentType/:id"
          element={<PaymentPage currentDevice={currentDevice} />}
        />
      </Routes>
    </Suspense>
  );
};

export default Router;
